/**
 * Copy text to the clipboard using the modern Clipboard API with fallback.
 *
 * @param text - The text to copy to the clipboard
 * @returns A promise that resolves to true if the copy was successful, false otherwise
 *
 * @example
 * ```typescript
 * const success = await copyToClipboard('Hello, world!');
 * if (success) {
 *   console.log('Copied to clipboard');
 * } else {
 *   console.error('Failed to copy');
 * }
 * ```
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed'; // Prevent scrolling to bottom
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    try {
      textArea.focus();
      textArea.select();
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
}
